import type { WorkflowType } from '@communities/content-management/domain';
import { Text, Heading, Dialog } from '@soteria/design-system-ve';

import * as styles from './MoreInfoDialog.css';

interface ProviderApplicationsMoreInfoDialogProps {
  isOpen: boolean;
  workflowType: WorkflowType;
  onClose: () => void;
}

export function ProviderApplicationsMoreInfoDialog({ isOpen, workflowType, onClose }: ProviderApplicationsMoreInfoDialogProps) {
  return (
    <Dialog cancelAction={{ label: 'Close' }} isOpen={isOpen} size="xxxl" title="About Provider Applications" onClose={onClose}>
      <div className={styles.dialogContent}>
        <Text>Provider Applications define which OAuth2 authentication providers and client applications can access your {workflowType} API endpoints.</Text>

        <div className={styles.section}>
          <Heading level="h3">Provider URL Configuration:</Heading>
          <ul className={styles.list}>
            <li className={styles.listItem}>
              <Text>Enter the base URL of your OAuth2 authentication provider</Text>
            </li>
            <li className={styles.listItem}>
              <Text>The system will automatically discover OpenID Connect configuration</Text>
            </li>
            <li className={styles.listItem}>
              <Text>Supports popular providers like Azure AD, Google, Auth0, and custom OAuth2 servers</Text>
            </li>
          </ul>
        </div>

        <div className={styles.section}>
          <Heading level="h3">Client Application IDs:</Heading>
          <ul className={styles.list}>
            <li className={styles.listItem}>
              <Text>List the client IDs of applications authorized to access {workflowType} APIs</Text>
            </li>
            <li className={styles.listItem}>
              <Text>Each provider can have multiple authorized client applications</Text>
            </li>
            <li className={styles.listItem}>
              <Text>Only pre-approved client IDs will be granted access to your endpoints</Text>
            </li>
          </ul>
        </div>

        <div className={styles.section}>
          <Heading level="h3">Security Benefits:</Heading>
          <ul className={styles.list}>
            <li className={styles.listItem}>
              <Text>Prevents unauthorized access to your {workflowType} APIs</Text>
            </li>
            <li className={styles.listItem}>
              <Text>Ensures only trusted applications can access your endpoints</Text>
            </li>
            <li className={styles.listItem}>
              <Text>Maintains audit trails of API access and usage</Text>
            </li>
            <li className={styles.listItem}>
              <Text>Supports role-based access control through OAuth2 scopes</Text>
            </li>
          </ul>
        </div>

        <Text>
          This configuration is essential for securing your {workflowType} workflows and ensuring only authorized applications can access your community's API
          capabilities.
        </Text>
      </div>
    </Dialog>
  );
}
